// src/components/BeerPairingInterface.js
/**
 * Advanced Beer Pairing Interface Component
 * "The Cicerone's Cipher v2" - Multi-Course Meal & Progressive Pairing Engine
 */

import React, { useState, useEffect } from "react";
import { advancedBeerPairingService } from "../services/advancedBeerPairingService";
import PropTypes from "prop-types";
import "./BeerPairingInterface.css";

const BeerPairingInterface = ({ compact = false, onPairingSelect = null }) => {
  const [selectedFood, setSelectedFood] = useState(null);
  const [selectedBeer, setSelectedBeer] = useState(null);
  const [pairingResult, setPairingResult] = useState(null);
  const [multiCourseMeal, setMultiCourseMeal] = useState([]);
  const [multiCourseResult, setMultiCourseResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("single"); // "single" or "multicourse"
  const [customFoodForm, setCustomFoodForm] = useState(false);
  const [customBeerForm, setCustomBeerForm] = useState(false);

  const [menuItems, setMenuItems] = useState([]);
  const [beers, setBeers] = useState([]);

  // Helper function for score badge classes
  const getScoreBadgeClass = (score) => {
    if (score > 10) return "excellent";
    if (score > 5) return "good";
    return "fair";
  };

  useEffect(() => {
    // Load initial data from service
    const menuData = advancedBeerPairingService.getMenuItems();
    const beerData = advancedBeerPairingService.getBeers();

    setMenuItems(menuData || []);
    setBeers(beerData || []);
  }, []);

  const handleSinglePairing = async () => {
    if (!selectedFood || !selectedBeer) return;

    setLoading(true);
    try {
      const result = advancedBeerPairingService.getSinglePairing(
        selectedFood,
        selectedBeer
      );
      setPairingResult(result);

      if (onPairingSelect) {
        onPairingSelect({
          type: "single",
          food: selectedFood,
          beer: selectedBeer,
          result,
        });
      }
    } catch (error) {
      console.error("Single pairing error:", error);
    }
    setLoading(false);
  };

  const handleMultiCoursePairing = async () => {
    if (multiCourseMeal.length === 0) return;

    setLoading(true);
    try {
      const result =
        advancedBeerPairingService.getMultiCoursePairing(multiCourseMeal);
      setMultiCourseResult(result);

      if (onPairingSelect) {
        onPairingSelect({
          type: "multicourse",
          courses: multiCourseMeal,
          result,
        });
      }
    } catch (error) {
      console.error("Multi-course pairing error:", error);
    }
    setLoading(false);
  };

  const addCourseToMeal = (food, beer) => {
    const newCourse = { food, beer };
    setMultiCourseMeal([...multiCourseMeal, newCourse]);
  };

  const removeCourseFromMeal = (index) => {
    const newMeal = multiCourseMeal.filter((_, i) => i !== index);
    setMultiCourseMeal(newMeal);
  };

  const addCustomFood = (foodData) => {
    const newFood = advancedBeerPairingService.addCustomFood(foodData);
    setMenuItems([...(menuItems || []), newFood]);
    setCustomFoodForm(false);
  };

  const addCustomBeer = (beerData) => {
    const newBeer = advancedBeerPairingService.addCustomBeer(beerData);
    setBeers([...(beers || []), newBeer]);
    setCustomBeerForm(false);
  };

  const renderSinglePairingTab = () => (
    <div className="single-pairing-tab">
      <div className="selection-grid">
        <div className="food-selection">
          <h3>🍽️ Select Food</h3>
          <div className="item-grid">
            {(menuItems || []).map((food) => (
              <button
                key={food.id}
                className={`item-card ${
                  selectedFood?.id === food.id ? "selected" : ""
                }`}
                onClick={() => setSelectedFood(food)}
                type="button"
              >
                <h4>{food.name}</h4>
                <div className="profile-preview">
                  <span>Intensity: {food.profile.intensity}/10</span>
                  <span>Cuisine: {food.profile.cuisine}</span>
                </div>
              </button>
            ))}
          </div>
          <button
            className="btn-secondary"
            onClick={() => setCustomFoodForm(true)}
          >
            + Add Custom Food
          </button>
        </div>

        <div className="beer-selection">
          <h3>🍺 Select Beer</h3>
          <div className="item-grid">
            {(beers || []).map((beer) => (
              <button
                key={beer.id}
                className={`item-card ${
                  selectedBeer?.id === beer.id ? "selected" : ""
                }`}
                onClick={() => setSelectedBeer(beer)}
                type="button"
              >
                <h4>{beer.name}</h4>
                <div className="profile-preview">
                  <span>ABV: {beer.profile.abv}%</span>
                  <span>IBU: {beer.profile.ibu}</span>
                  <span>Style: {beer.profile.region}</span>
                </div>
              </button>
            ))}
          </div>
          <button
            className="btn-secondary"
            onClick={() => setCustomBeerForm(true)}
          >
            + Add Custom Beer
          </button>
        </div>
      </div>

      <div className="pairing-actions">
        <button
          className="btn-primary"
          onClick={handleSinglePairing}
          disabled={!selectedFood || !selectedBeer || loading}
        >
          {loading ? "Analyzing Pairing..." : "🔍 Analyze Pairing"}
        </button>
      </div>

      {pairingResult && (
        <div className="pairing-result">
          <div className="result-header">
            <h3>🎯 Pairing Analysis</h3>
            <div
              className={`score-badge ${getScoreBadgeClass(
                pairingResult.score
              )}`}
            >
              Score: {pairingResult.score.toFixed(1)}/20
            </div>
          </div>

          <div className="rationale-list">
            <h4>💡 Why This Works:</h4>
            {pairingResult.rationale.map((item, index) => (
              <div
                key={`rationale-${index}-${item.p}`}
                className="rationale-item"
              >
                <span className="points">{item.p}</span>
                <span className="reason">{item.r}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderMultiCourseTab = () => (
    <div className="multicourse-tab">
      <div className="course-builder">
        <h3>🍽️ Build Your Multi-Course Meal</h3>

        <div className="course-adder">
          <select
            value={selectedFood?.id || ""}
            onChange={(e) =>
              setSelectedFood(
                (menuItems || []).find((f) => f.id === parseInt(e.target.value))
              )
            }
          >
            <option value="">Select Food Course</option>
            {(menuItems || []).map((food) => (
              <option key={food.id} value={food.id}>
                {food.name}
              </option>
            ))}
          </select>

          <select
            value={selectedBeer?.id || ""}
            onChange={(e) =>
              setSelectedBeer(
                (beers || []).find((b) => b.id === parseInt(e.target.value))
              )
            }
          >
            <option value="">Select Beer Pairing</option>
            {(beers || []).map((beer) => (
              <option key={beer.id} value={beer.id}>
                {beer.name}
              </option>
            ))}
          </select>

          <button
            className="btn-secondary"
            onClick={() => addCourseToMeal(selectedFood, selectedBeer)}
            disabled={!selectedFood || !selectedBeer}
          >
            + Add Course
          </button>
        </div>

        <div className="course-list">
          {multiCourseMeal.map((course, index) => (
            <div
              key={`course-${index}-${course.food.id}-${course.beer.id}`}
              className="course-item"
            >
              <span className="course-number">Course {index + 1}</span>
              <span className="course-food">{course.food.name}</span>
              <span className="course-pairing">with {course.beer.name}</span>
              <button
                className="btn-remove"
                onClick={() => removeCourseFromMeal(index)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <button
          className="btn-primary"
          onClick={handleMultiCoursePairing}
          disabled={multiCourseMeal.length === 0 || loading}
        >
          {loading ? "Analyzing Meal..." : "🎯 Analyze Full Meal"}
        </button>
      </div>

      {multiCourseResult && (
        <div className="multicourse-result">
          <div className="result-header">
            <h3>🍽️ Multi-Course Analysis</h3>
            <div
              className={`score-badge ${multiCourseResult.overallHarmony.rating.toLowerCase()}`}
            >
              {multiCourseResult.overallHarmony.rating}
            </div>
            <div className="total-score">
              Total Score: {multiCourseResult.totalScore.toFixed(1)}
            </div>
          </div>

          <div className="course-results">
            {multiCourseResult.results.map((result, index) => (
              <div
                key={`course-result-${index}-${result.food.id}`}
                className="course-result"
              >
                <h4>
                  Course {index + 1}: {result.food.name} + {result.beer.name}
                </h4>
                <div className="course-score">
                  Score: {result.score.toFixed(1)}/20
                </div>
                <div className="course-rationale">
                  {result.rationale.slice(0, 3).map((item, i) => (
                    <div
                      key={`rationale-mini-${index}-${i}-${item.p}`}
                      className="rationale-item-mini"
                    >
                      <span className="points">{item.p}</span>
                      <span className="reason">{item.r}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (compact) {
    return (
      <div className="beer-pairing-interface compact">
        <div className="compact-header">
          <h3>🍺 Beer Pairing Engine</h3>
          <span className="powered-by">Powered by Cicerone's Cipher v2</span>
        </div>
        {/* Simplified compact view */}
        <div className="compact-quick-pairing">
          <select
            onChange={(e) =>
              setSelectedFood(
                (menuItems || []).find((f) => f.id === parseInt(e.target.value))
              )
            }
          >
            <option value="">Select Food</option>
            {(menuItems || []).map((food) => (
              <option key={food.id} value={food.id}>
                {food.name}
              </option>
            ))}
          </select>
          <button
            className="btn-primary"
            onClick={() => {
              if (selectedFood) {
                const recommendations =
                  advancedBeerPairingService.getTopRecommendations(
                    selectedFood,
                    beers || [],
                    1
                  );
                const topBeer =
                  recommendations && recommendations.length > 0
                    ? recommendations[0]
                    : null;
                if (topBeer && onPairingSelect) {
                  onPairingSelect({
                    type: "quick",
                    food: selectedFood,
                    beer: topBeer.beer,
                    result: topBeer,
                  });
                }
              }
            }}
            disabled={!selectedFood}
          >
            Quick Pair
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="beer-pairing-interface">
      <div className="interface-header">
        <h2>🍺 Advanced Beer Pairing Engine</h2>
        <p className="subtitle">
          The Cicerone's Cipher v2 - Multi-Course Meal & Progressive Pairing
        </p>
      </div>

      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === "single" ? "active" : ""}`}
          onClick={() => setActiveTab("single")}
        >
          Single Pairing
        </button>
        <button
          className={`tab-btn ${activeTab === "multicourse" ? "active" : ""}`}
          onClick={() => setActiveTab("multicourse")}
        >
          Multi-Course Meal
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "single"
          ? renderSinglePairingTab()
          : renderMultiCourseTab()}
      </div>

      {/* Custom Food Form Modal */}
      {customFoodForm && (
        <CustomFoodModal
          onSubmit={addCustomFood}
          onClose={() => setCustomFoodForm(false)}
        />
      )}

      {/* Custom Beer Form Modal */}
      {customBeerForm && (
        <CustomBeerModal
          onSubmit={addCustomBeer}
          onClose={() => setCustomBeerForm(false)}
        />
      )}
    </div>
  );
};

// Custom Food Modal Component
const CustomFoodModal = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    profile: {
      fat: 5,
      acidity: 5,
      sweetness: 5,
      bitterness: 5,
      umami: 5,
      salt: 5,
      spice: 5,
      intensity: 5,
      texture: "soft",
      temp: "hot",
      cookingMethod: "grilling",
      aromaCompounds: [],
      problemIngredients: [],
      cuisine: "American",
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Add Custom Food Item</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Food Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <div className="profile-sliders">
            {Object.entries(formData.profile).map(([key, value]) => {
              if (typeof value === "number") {
                return (
                  <div key={key} className="slider-group">
                    <label>
                      {key.charAt(0).toUpperCase() + key.slice(1)}: {value}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={value}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          profile: {
                            ...formData.profile,
                            [key]: parseInt(e.target.value),
                          },
                        })
                      }
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit">Add Food</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Custom Beer Modal Component
const CustomBeerModal = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    profile: {
      ibu: 30,
      abv: 5.0,
      sweetness: 5,
      acidity: 5,
      carbonation: 5,
      body: 5,
      intensity: 5,
      maillard: 5,
      caramel: 5,
      fruityEster: 5,
      spicyPhenol: 5,
      idealTemp: 7,
      color: "gold",
      region: "American",
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Add Custom Beer</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Beer Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <div className="profile-sliders">
            {Object.entries(formData.profile).map(([key, value]) => {
              if (typeof value === "number") {
                let max = 10;
                if (key === "ibu") max = 100;
                else if (key === "abv") max = 15;
                const step = key === "abv" ? 0.1 : 1;
                return (
                  <div key={key} className="slider-group">
                    <label>
                      {key.toUpperCase()}: {value}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max={max}
                      step={step}
                      value={value}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          profile: {
                            ...formData.profile,
                            [key]: parseFloat(e.target.value),
                          },
                        })
                      }
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit">Add Beer</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PropTypes for modal components
CustomFoodModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CustomBeerModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BeerPairingInterface.propTypes = {
  compact: PropTypes.bool,
  onPairingSelect: PropTypes.func,
};

export default BeerPairingInterface;
